/* ApproversScreen (DESIGN40) — ROOT ONLY. Who approves each pipeline step
   and how many votes it takes — the same policies the web Root panel edits,
   on a phone. Four cards in pipeline order: a 1–5 stepper and the eligible
   accounts as toggles (the server's own eligibility list: root or the
   step's sub-role, disabled accounts excluded). An empty list means «anyone
   who holds the right» — today's default, spelled out on the card.

   Writing a policy touches only the policy tables: nothing already approved
   is ever affected. Architecture may use an author-to-general-reviewer flow;
   Pricing uses one selected person. Other document policies keep their existing controls. */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  APPROVAL_POLICY_STEPS, ApprovalPolicies, ApprovalPolicyStep, ApproverOption, approvalErrorText, approvalsApi,
} from '../../architect/api';
import { haptic, useBackButton } from '../telegram';
import StagePolicyPreferences, { StagePolicyNotifications, PolicyPreferences, policyPreferences, samePolicyPreferences } from '../../architect/StagePolicyPreferences';
import { approvalPolicyDraft } from '../../architect/approvalPolicyDraft';

interface Props {
  onBack: () => void;
}

const STEP_LABEL: Record<ApprovalPolicyStep, string> = {
  architecture: 'Architecture',
  estimate: 'Estimate',
  pricing: 'Pricing',
  proposal: 'Proposal',
};
const STEP_HINT: Record<ApprovalPolicyStep, string> = {
  architecture: 'Review the architecture',
  estimate: 'Review the development estimate',
  pricing: 'Review setup and monthly prices',
  proposal: 'Review the final proposal',
};

interface Draft extends PolicyPreferences {
  required: number;
  /** In approval order when `ordered`. */
  ids: string[];
  /** DESIGN46 «await» — one after another. */
  ordered: boolean;
  generalReview?: boolean;
  generalReviewerId?: string | null;
  incompleteNamedApprovers?: boolean;
}

const MAX_REQUIRED = 5;

export default function ApproversScreen({ onBack }: Props) {
  useBackButton(true, onBack);

  const [saved, setSaved] = useState<Record<string, Draft> | null>(null);
  const [draft, setDraft] = useState<Record<string, Draft>>({});
  const [eligible, setEligible] = useState<Partial<Record<ApprovalPolicyStep, ApproverOption[]>>>({});
  const [accessMetadata, setAccessMetadata] = useState<Pick<ApprovalPolicies, 'access_defaults' | 'root_user_ids'>>({});
  const [error, setError] = useState<string | null>(null);
  const [savingStep, setSavingStep] = useState<ApprovalPolicyStep | null>(null);
  const [savedStep, setSavedStep] = useState<ApprovalPolicyStep | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await approvalsApi.getPolicies();
      const next: Record<string, Draft> = {};
      for (const step of APPROVAL_POLICY_STEPS) {
        const p = r.policies.find((x) => x.step === step);
        next[step] = {
          ...approvalPolicyDraft(p),
          ...policyPreferences(p),
          ...(step === 'architecture' || p?.general_reviewer_id !== undefined ? { generalReview: Boolean(p?.general_reviewer_id), generalReviewerId: p?.general_reviewer_id ?? null } : {}),
        };
      }
      setAccessMetadata({ access_defaults: r.access_defaults, root_user_ids: r.root_user_ids });
      setSaved(next);
      setDraft(next);
      setEligible(Object.fromEntries(APPROVAL_POLICY_STEPS.map((step) => {
        const p = r.policies.find((x) => x.step === step);
        return [step, r.eligible?.[step] || [...(p?.users || []), ...(p?.general_reviewer ? [p.general_reviewer] : [])].filter((u, index, all) => all.findIndex((person) => person.id === u.id) === index)];
      })));
      setError(null);
    } catch (err) {
      setError(approvalErrorText(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const isDirty = useCallback(
    (step: ApprovalPolicyStep) => {
      const a = draft[step];
      const b = saved?.[step];
      if (!a || !b) return false;
      if (!samePolicyPreferences(a, b)) return true;
      if (Boolean(a.generalReview) !== Boolean(b.generalReview)) return true;
      if (a.generalReview && a.generalReviewerId !== b.generalReviewerId) return true;
      const ao = a.ordered && a.ids.length > 1;
      const bo = b.ordered && b.ids.length > 1;
      if (a.required !== b.required || ao !== bo) return true;
      return ao ? a.ids.join() !== b.ids.join() : a.ids.slice().sort().join() !== b.ids.slice().sort().join();
    },
    [draft, saved],
  );

  const setRequired = (step: ApprovalPolicyStep, delta: number) => {
    haptic.impact('light');
    setDraft((prev) => {
      const cur = prev[step] || { required: 1, ids: [], ordered: false };
      const required = Math.min(MAX_REQUIRED, Math.max(1, cur.required + delta));
      return { ...prev, [step]: { ...cur, required } };
    });
  };

  const toggleUser = (step: ApprovalPolicyStep, id: string) => {
    haptic.impact('light');
    setDraft((prev) => {
      const cur = prev[step] || { required: 1, ids: [], ordered: false };
      if (cur.generalReview) {
        return { ...prev, [step]: { ...cur, generalReviewerId: id } };
      }
      const ids = cur.ids.includes(id) ? cur.ids.filter((x) => x !== id) : cur.ids.concat(id);
      return { ...prev, [step]: { ...cur, ids, required: ids.length ? Math.min(cur.required, ids.length) : cur.required } };
    });
  };

  const setGeneralReviewMode = (step: ApprovalPolicyStep, generalReview: boolean) => {
    haptic.impact('light');
    setSavedStep(null);
    setError(null);
    setDraft((prev) => ({ ...prev, [step]: { ...prev[step], generalReview } }));
  };

  const moveApprover = (step: ApprovalPolicyStep, index: number, direction: number) => {
    haptic.impact('light'); setSavedStep(null);
    setDraft(prev => {
      const current = prev[step];
      const ids = [...current.ids];
      const destination = index + direction;
      if (destination < 0 || destination >= ids.length) return prev;
      [ids[index], ids[destination]] = [ids[destination], ids[index]];
      return { ...prev, [step]: { ...current, ids } };
    });
  };

  const toggleOrdered = (step: ApprovalPolicyStep) => {
    haptic.impact('light');
    setDraft((prev) => {
      const cur = prev[step] || { required: 1, ids: [], ordered: false };
      const ordered = !cur.ordered;
      // «Await» means everyone in the line signs — switching it on raises the
      // count to the list length (root can lower it again).
      const required = ordered ? Math.min(MAX_REQUIRED, Math.max(cur.required, cur.ids.length)) : cur.required;
      return { ...prev, [step]: { ...cur, ordered, required } };
    });
  };

  const save = useCallback(
    async (step: ApprovalPolicyStep) => {
      const d = draft[step];
      if (!d || savingStep || d.incompleteNamedApprovers) return;
      if (d.generalReview && !d.generalReviewerId) return;
      haptic.impact('medium');
      setSavingStep(step);
      setSavedStep(null);
      setError(null);
      try {
        const r = await approvalsApi.putPolicy(step, {
          required_count: d.required, user_ids: d.ids, ordered: d.ordered,
          ...policyPreferences(d),
          ...(step === 'architecture' || d.generalReview !== undefined ? { general_reviewer_id: d.generalReview ? d.generalReviewerId : null } : {}),
        });
        const stored: Draft = {
          ...approvalPolicyDraft(r.policy),
          ...policyPreferences(r.policy),
          ...(step === 'architecture' || r.policy?.general_reviewer_id !== undefined ? { generalReview: Boolean(r.policy?.general_reviewer_id), generalReviewerId: r.policy?.general_reviewer_id ?? null } : {}),
        };
        setSaved((prev) => ({ ...(prev || {}), [step]: stored }));
        setDraft((prev) => ({ ...prev, [step]: stored }));
        setSavedStep(step);
        haptic.notify('success');
        window.setTimeout(() => setSavedStep((cur) => (cur === step ? null : cur)), 2000);
      } catch (err) {
        haptic.notify('error');
        setError(STEP_LABEL[step] + ': ' + approvalErrorText(err));
      } finally {
        setSavingStep(null);
      }
    },
    [draft, savingStep],
  );

  const nameOf = (u: ApproverOption) => u.display_name || u.username;

  const cards = useMemo(
    () =>
      APPROVAL_POLICY_STEPS.map((step, index) => {
        const d = draft[step] || { required: 1, ids: [], ordered: false };
        const opts = eligible[step] || [];
        const unavailableIds = d.ids.filter(id => !opts.some(person => person.id === id));
        const dirty = isDirty(step);
        const isPricing = step === 'pricing';
        const generalReview = Boolean(d.generalReview);
        const singlePerson = generalReview;
        const generalReviewer = opts.find((u) => u.id === d.generalReviewerId);
        const inOrder = !singlePerson && d.ordered && d.ids.length > 1;
        // In approval order when ordered (the draft's own order), else as listed.
        const named = inOrder
          ? d.ids.map(id => opts.find(u => u.id === id) || { id, username: 'Unavailable reviewer', display_name: null })
          : [...opts.filter(u => d.ids.includes(u.id)), ...unavailableIds.map(id => ({ id, username: 'Unavailable reviewer', display_name: null }))];
        // A threshold nobody can reach is a stuck pipeline — say so before Save.
        const unreachable = !generalReview && d.ids.length > 0 && d.required > d.ids.length;
        return (
          <div className={'tma-card tma-policy' + (dirty ? ' dirty' : '')} key={step} role="region" aria-label={STEP_LABEL[step] + ' policy'}>
            <div className="tma-policy-head">
              <span className="tma-policy-index" aria-hidden="true">{index + 1}</span>
              <div className="tma-policy-heading-copy">
                <div className="tma-policy-title">{STEP_LABEL[step]}</div>
                <div className="tma-policy-hint">{STEP_HINT[step]}</div>
              </div>
            </div>

            <StagePolicyPreferences value={d} people={opts} defaults={accessMetadata.access_defaults?.[step]} rootIds={accessMetadata.root_user_ids} disabled={savingStep === step} onChange={patch => {
              setSavedStep(null); setError(null);
              setDraft(prev => ({ ...prev, [step]: { ...prev[step], ...patch } }));
            }} />

            {<>
              <div className="tma-policy-label">Approval flow</div>
              <div className="tma-rights" role="group" aria-label={STEP_LABEL[step] + ' approval flow'}>
                <button type="button" className={'tma-right' + (!generalReview ? ' on' : '')} aria-pressed={!generalReview} disabled={savingStep === step} onClick={() => setGeneralReviewMode(step, false)}>Named approvers</button>
                <button type="button" className={'tma-right' + (generalReview ? ' on' : '')} aria-pressed={generalReview} disabled={savingStep === step} onClick={() => setGeneralReviewMode(step, true)}>Author → General reviewer</button>
              </div>
            </>}

            {!singlePerson && <div className="tma-policy-row">
              <span className="tma-policy-label">Approvals needed</span>
              <div className="tma-stepper" role="group" aria-label="Approvals needed">
                <button type="button" className="tma-stepper-btn" disabled={savingStep === step || d.required <= 1} onClick={() => setRequired(step, -1)} aria-label="Fewer">−</button>
                <span className="tma-stepper-val">{d.required}</span>
                <button type="button" className="tma-stepper-btn" disabled={savingStep === step || d.required >= MAX_REQUIRED} onClick={() => setRequired(step, +1)} aria-label="More">+</button>
              </div>
            </div>}

            <div className="tma-policy-label">{generalReview ? 'General reviewer' : 'Who may approve'}</div>
            {d.incompleteNamedApprovers ? <p className="stage-policy-note" role="alert">Reviewer list unavailable. Reload before saving this stage.</p>
              : generalReview && d.generalReviewerId && !generalReviewer ? <p className="stage-policy-note" role="status">The General reviewer is unavailable. Choose a replacement to resume reviews.</p>
              : !generalReview && unavailableIds.length > 0 ? <p className="stage-policy-note" role="status">Unavailable reviewers are kept until you remove them.</p> : null}
            {opts.length ? (
              <div className="tma-rights" role={singlePerson ? 'radiogroup' : 'group'} aria-label={generalReview ? 'General reviewer' : STEP_LABEL[step] + ' approvers'}>
                {opts.map((u) => {
                  const on = generalReview ? d.generalReviewerId === u.id : d.ids.includes(u.id);
                  const pos = inOrder && on ? d.ids.indexOf(u.id) + 1 : 0;
                  return (
                    <button
                      type="button"
                      key={u.id}
                      className={'tma-right' + (on ? ' on' : '') + (pos ? ' numbered' : '')}
                      role={singlePerson ? 'radio' : undefined}
                      aria-checked={singlePerson ? on : undefined}
                      aria-pressed={singlePerson ? undefined : on}
                      disabled={savingStep === step}
                      onKeyDown={singlePerson ? (e) => {
                        if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'].includes(e.key)) return;
                        e.preventDefault();
                        const offset = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
                        const next = opts[(opts.indexOf(u) + offset + opts.length) % opts.length];
                        toggleUser(step, next.id);
                        const radios = e.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
                        radios?.[opts.indexOf(next)]?.focus();
                      } : undefined}
                      onClick={() => toggleUser(step, u.id)}
                    >
                      {pos ? <span className="tma-right-num">{pos}</span> : null}
                      {nameOf(u)}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="tma-policy-empty">{isPricing ? 'Grant the Pricing sub-role in Team.' : 'No eligible accounts yet — grant the sub-role in Team.'}</div>
            )}
            {!generalReview && unavailableIds.length > 0 && <div className="tma-rights">{unavailableIds.map(id => <button key={id}
              type="button" className="tma-right on" disabled={savingStep === step} title={id}
              aria-label={`Remove unavailable reviewer ${id}`} onClick={() => toggleUser(step, id)}>Unavailable reviewer ×</button>)}</div>}
            {!singlePerson && d.ids.length > 1 ? (
              <div className="tma-policy-row tma-policy-order">
                <span className="tma-policy-label">
                  Approve in order
                  <span className="tma-policy-order-hint">
                    {inOrder
                      ? 'Each person is asked after the previous approval. Use the arrows to change the order.'
                      : 'Everyone named is asked at the same time.'}
                  </span>
                </span>
                <button
                  type="button"
                  className={'tma-switch' + (d.ordered ? ' on' : '')}
                  role="switch"
                  aria-checked={d.ordered}
                  aria-label="Approve in order"
                  disabled={savingStep === step}
                  onClick={() => toggleOrdered(step)}
                >
                  <span className="tma-switch-knob" />
                </button>
              </div>
            ) : null}
            {inOrder && <div className="tma-policy-sequence" aria-label="Approval order">
              {d.ids.map((id, position) => {
                const personName = opts.find(person => person.id === id)?.display_name || opts.find(person => person.id === id)?.username || 'Unavailable account';
                return <div key={id}><span>{position + 1}. {personName}</span>
                  <button type="button" className="tma-btn tma-btn-ghost" aria-label={`Move ${personName} earlier`} disabled={savingStep === step || position === 0} onClick={() => moveApprover(step, position, -1)}>↑</button>
                  <button type="button" className="tma-btn tma-btn-ghost" aria-label={`Move ${personName} later`} disabled={savingStep === step || position === d.ids.length - 1} onClick={() => moveApprover(step, position, 1)}>↓</button>
                </div>;
              })}
            </div>}
            <div className="tma-policy-sum">
              {d.incompleteNamedApprovers ? 'Reviewers unavailable'
                : generalReview
                ? generalReviewer
                  ? `Author confirms and submits → ${nameOf(generalReviewer)} gives final approval. Their own ${step === 'architecture' ? 'architectures' : 'documents'} need only their approval.`
                  : d.generalReviewerId ? 'Author confirms and submits → unavailable General reviewer.' : 'Choose a General reviewer. Each author confirms and submits before the final review.'
                : d.ids.length === 0
                ? d.access != null ? 'No approvers selected. Root can approve.' : isPricing ? 'Choose an approver to enable pricing requests.' : `Anyone with the right may approve · ${d.required} vote${d.required > 1 ? 's' : ''}`
                : `${named.map(nameOf).join(inOrder ? ' → ' : ', ')} · ${d.required} of ${d.ids.length}`}
              {!d.incompleteNamedApprovers && !singlePerson && !isPricing && !inOrder && !(d.access != null && !d.ids.length) ? ' · root can also approve' : ''}
            </div>
            {unreachable ? (
              <div className="tma-banner warn">
                {d.required} approvals but only {d.ids.length} named — the step could never complete. Name more people or lower the count.
              </div>
            ) : null}

            <StagePolicyNotifications value={d} people={opts} step={step} defaults={accessMetadata.access_defaults?.[step]} rootIds={accessMetadata.root_user_ids} disabled={savingStep === step} onChange={patch => {
              setSavedStep(null); setError(null);
              setDraft(prev => ({ ...prev, [step]: { ...prev[step], ...patch } }));
            }} />

            {dirty || savedStep === step ? (
              <div className="tma-policy-actions">
                <button
                  type="button"
                  className="tma-btn tma-btn-primary tma-btn-sm"
                  disabled={!dirty || savingStep === step || unreachable || d.incompleteNamedApprovers || (generalReview && !d.generalReviewerId)}
                  onClick={() => save(step)}
                >
                  {savingStep === step ? 'Saving…' : savedStep === step && !dirty ? 'Saved ✓' : 'Save'}
                </button>
                {dirty ? (
                  <button
                    type="button"
                    className="tma-btn tma-btn-ghost tma-btn-sm"
                    disabled={savingStep === step}
                    onClick={() => setDraft((prev) => ({ ...prev, [step]: saved?.[step] || { required: 1, ids: [], ordered: false } }))}
                  >
                    Discard
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      }),
    [draft, eligible, accessMetadata, isDirty, save, saved, savedStep, savingStep],
  );

  return (
    <div className="tma-screen tma-approvers">
      <div className="tma-projects-inner">
        <h1 className="tma-title">Stage access</h1>
        <p className="tma-team-lead">
          Root can always view, edit and approve every stage.
        </p>
        {error ? <div className="tma-banner err" role="alert">{error}</div> : null}
        {saved === null && error ? (
          <div className="tma-empty" role="status"><div className="tma-empty-title">Approval rules could not load</div><div className="tma-empty-sub">Check your connection and try again.</div><button type="button" className="tma-btn tma-btn-primary" onClick={() => void load()}>Try again</button></div>
        ) : saved === null ? (
          <div className="tma-cards">
            {[0, 1, 2, 3].map((i) => (
              <div className="tma-card tma-card-skel" key={i}>
                <div className="tma-skel tma-skel-name" />
                <div className="tma-skel tma-skel-meta" />
              </div>
            ))}
          </div>
        ) : (
          <div className="tma-cards">{cards}</div>
        )}
      </div>
    </div>
  );
}
